import { useState } from "react";
import { Card, BlockStack, Badge, Banner, Button, DataTable, InlineStack, Spinner, Text } from "@shopify/polaris";
import { api, ApiRequestError } from "../api";
import type { WeightCoverage } from "../types";

// Walks the catalogue, so it is not run on page load — same reasoning as the
// delivery-sources scan next to it.
export default function WeightCoverageCard(): JSX.Element {
  const [coverage, setCoverage] = useState<WeightCoverage | null>(null);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function runScan(refresh: boolean): Promise<void> {
    setScanning(true);
    setError(null);
    try {
      setCoverage(await api.get<WeightCoverage>(`/weight-coverage${refresh ? "?refresh=1" : ""}`));
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.body.message : "Could not scan products");
    } finally {
      setScanning(false);
    }
  }

  return (
    <Card>
      <BlockStack gap="300">
        <Text as="h2" variant="headingMd">
          Product weights
        </Text>
        <Text as="p" tone="subdued">
          ShipPromise buys postage at the weight Shopify holds for each variant. A product with no
          weight, or a weight of 0 (Shopify&apos;s form default), falls back to 16 oz,
          and that guess is what gets charged. Fixing it means setting the weight on the product in
          Shopify.
        </Text>

        {error && <Banner tone="critical">{error}</Banner>}

        <InlineStack gap="200" blockAlign="center">
          <Button onClick={() => void runScan(coverage !== null)} loading={scanning}>
            {coverage ? "Rescan products" : "Check product weights"}
          </Button>
          {coverage && (
            <Text as="span" tone="subdued" variant="bodySm">
              {coverage.shippable_count} shippable variant{coverage.shippable_count === 1 ? "" : "s"} checked{" "}
              {new Date(coverage.scanned_at).toLocaleString()}
            </Text>
          )}
        </InlineStack>

        {scanning && !coverage ? <Spinner accessibilityLabel="Checking product weights" size="small" /> : null}

        {coverage && coverage.unweighed_count === 0 && (
          <Banner tone="success">
            Every shippable variant has a weight. No label will be bought at a guessed weight.
          </Banner>
        )}

        {coverage && coverage.unweighed_count > 0 && (
          <BlockStack gap="200">
            <InlineStack gap="150" blockAlign="center">
              <Badge tone="warning">{`${coverage.unweighed_count} without a weight`}</Badge>
              {coverage.truncated && (
                <Text as="span" tone="subdued" variant="bodySm">
                  Catalogue larger than the scan limit. There may be more.
                </Text>
              )}
            </InlineStack>
            <DataTable
              columnContentTypes={["text", "text"]}
              headings={["SKU", "Product"]}
              rows={coverage.unweighed.map((variant) => [variant.sku ?? "—", variant.title])}
            />
            {coverage.unweighed.length < coverage.unweighed_count && (
              <Text as="p" tone="subdued" variant="bodySm">
                Showing the first {coverage.unweighed.length} of {coverage.unweighed_count}.
              </Text>
            )}
          </BlockStack>
        )}
      </BlockStack>
    </Card>
  );
}
